'use strict'

interface ParameterExclusionConditions {
  [key: string]: string | number
}

interface Hash {
  anchor: string | null
  parameters: Record<string, string>
  parameterExclusionConditions: ParameterExclusionConditions
}

interface HashManagerConfig {
  updateHashOnParametersChange: boolean
}

const savedHash: Hash = {
  anchor: null,
  parameters: {},
  parameterExclusionConditions: {},
}

const defaultConfig: HashManagerConfig = {
  updateHashOnParametersChange: true,
}

export default {
  config: defaultConfig,

  init({ parameterExclusionConditions = {} } = {}, config?: HashManagerConfig) {
    if (!config) {
      config = defaultConfig
    }

    this.setConfig(config)
    savedHash.parameterExclusionConditions = parameterExclusionConditions
    this.parseHash()
  },

  parseHash() {
    const hash = window.location.hash.match(/#([a-zA-Z0-9-]*)(\?.*)?/)
    savedHash.anchor = null
    savedHash.parameters = {}

    if (hash) {
      if (hash[1]) {
        savedHash.anchor = hash[1]

        requestAnimationFrame(() => {
          this.goToAnchor(savedHash.anchor as string)
        })
      }

      if (hash[2]) {
        savedHash.parameters = Object.fromEntries(new URLSearchParams(decodeURIComponent(hash[2])))
      }
    }

    return this
  },

  setAnchor(anchor: string) {
    savedHash.anchor = anchor

    this.updateHash()

    return this
  },

  getAnchor() {
    return savedHash.anchor
  },

  goToAnchor(anchor: string): void {
    const anchorEl = document.getElementById(anchor)

    if (anchorEl) {
      anchorEl.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      })
    }
  },

  removeAnchor() {
    savedHash.anchor = null

    this.updateHash()

    return this
  },

  updateHash() {
    let hash = ''

    if (savedHash.anchor) {
      hash = savedHash.anchor + hash
    }

    const parameters = this.getParametersWithExclusion()

    if (Object.keys(parameters).length > 0) {
      const entries = Object.entries(parameters)
        .filter(([, v]) => v !== '' && v != null)
        .sort(([k1], [k2]) => k1.localeCompare(k2))

      hash = hash + '?' + new URLSearchParams(entries).toString()
    }

    history.pushState({}, '', `#${hash}`)

    return this
  },

  hasParameters() {
    return Object.keys(savedHash.parameters).length > 0
  },

  setParameters(parameters = {}, replaceAll = false) {
    if (replaceAll) {
      savedHash.parameters = parameters
    } else {
      savedHash.parameters = Object.assign({}, savedHash.parameters, parameters)
    }

    if (!this.config.updateHashOnParametersChange) {
      return this
    }

    this.updateHash()

    return this
  },

  getParametersWithExclusion() {
    const parameters = this.getParameters()

    Object.keys(savedHash.parameterExclusionConditions).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(parameters, key) && parameters[key] === savedHash.parameterExclusionConditions[key]) {
        delete parameters[key]
      }
    })

    return parameters
  },

  getParameters() {
    return Object.assign({}, savedHash.parameters)
  },

  removeParameters() {
    savedHash.parameters = {}

    this.updateHash()

    return this
  },

  getParameter(key: string) {
    if (!savedHash.parameters[key]) {
      return ''
    }

    return savedHash.parameters[key] ?? ''
  },

  setConfig(config: HashManagerConfig) {
    this.config = config
  },

  startUpdatingHash() {
    this.config.updateHashOnParametersChange = true
  },

  stopUpdatingHash() {
    this.config.updateHashOnParametersChange = false
  },

  destroy() {
    savedHash.anchor = null
    savedHash.parameters = {}

    return this
  },
}
